// szczegóły produktu - lokalna wersja
// @ts-nocheck

import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { ChevronDown, X, Check2 } from 'react-bootstrap-icons';
import Tooltip from '@mui/material/Tooltip';
import { Trans } from 'react-i18next';

import { useAppNavigate } from 'hooks';
import { IProduct, ITechnicalAttribute, IProductVersion, IVariant } from 'api/types';
import { Select, Link, Modal, Button } from 'components/controls';
import { hasAttributeIcon } from 'utils/productAttributesWithIcons';

// Import ikon atrybutów produktu
import { ReactComponent as DoZamrozeniaIcon } from 'assets/icons/product-attributes/Nadaje się do zamrażania_icon.svg';
import { ReactComponent as DoMikrofalowkiIcon } from 'assets/icons/product-attributes/Nadaje się do mikrofalówki_icon.svg';
import { ReactComponent as DoCieplychProduktowIcon } from 'assets/icons/product-attributes/Nadaje się do ciepłych produktow_icon.svg';
import { ReactComponent as TylkoDoZimnychProduktowIcon } from 'assets/icons/product-attributes/Tylko do zimnych produktów_icon.svg';
import { ReactComponent as DoZmywarkiIcon } from 'assets/icons/product-attributes/Nadaje się do zmywarki_icon.svg';
import { ReactComponent as BiodegradowalneIcon } from 'assets/icons/product-attributes/Biodegradowalne-2_icon.svg';
import { ReactComponent as DoRecyklinguIcon } from 'assets/icons/product-attributes/Do recyklingu_icon.svg';
import { ReactComponent as PodNadrukIcon } from 'assets/icons/product-attributes/Nadaje się pod nadruk_icon.svg';
import { ReactComponent as ZRecyklinguIcon } from 'assets/icons/product-attributes/Pochodzi z recyklingu-1_icon.svg';
import { ReactComponent as ZRoslinIcon } from 'assets/icons/product-attributes/Wytworzone z roślin_icon.svg';
import { ReactComponent as DoPosadzekIcon } from 'assets/icons/product-attributes/Do posadzek_icon.svg';
import { ReactComponent as DoSanitariatowIcon } from 'assets/icons/product-attributes/Do sanitariatów_icon.svg';
import { ReactComponent as NeutralizacjaZapachowIcon } from 'assets/icons/product-attributes/Neutralizacja zapachów_icon.svg';
import { ReactComponent as PowierzchnieSzlaneIcon } from 'assets/icons/product-attributes/Powierzchnie szklane_icon.svg';
import { ReactComponent as DoDrewnaIcon } from 'assets/icons/product-attributes/Do drewna_icon.svg';
import { ReactComponent as DoStaliNierdzewnejIcon } from 'assets/icons/product-attributes/Do stali nierdzewnej_icon.svg';
import { ReactComponent as DlaGastronomiiIcon } from 'assets/icons/product-attributes/Dla gastronomii_icon.svg';
import { ReactComponent as DywanyIWykadzinyIcon } from 'assets/icons/product-attributes/Dywany i wykładziny_icon.svg';
import { ReactComponent as PrzemyslIcon } from 'assets/icons/product-attributes/Przemysł_icon.svg';
import { ReactComponent as DezynfekcjaIcon } from 'assets/icons/product-attributes/Dezynfekcja_icon.svg';
import { ReactComponent as MycieReczneIcon } from 'assets/icons/product-attributes/Mycie ręczne_icon.svg';
import { ReactComponent as MycieMaszynoweIcon } from 'assets/icons/product-attributes/Mycie maszynowe_icon.svg';
import { ReactComponent as MycieMopemIcon } from 'assets/icons/product-attributes/Mycie mopem_icon.svg';
import { ReactComponent as SpryskiwaczIcon } from 'assets/icons/product-attributes/Spryskiwacz_icon.svg';
import { ReactComponent as DoSystemuDozowaniaIcon } from 'assets/icons/product-attributes/Do systemu dozowania_icon.svg';

// Import lokalnych stylów, które nadpisują style z core
import styles from 'theme/pages/Product/components/Details/Details.module.scss';

// typ danych wejściowych
interface IProps {
  product: IProduct;
  scrollToDescription: () => void;
  hideTitleAndMetadata?: boolean;
  /** SEO H1 z url_resolver (np. z CMS) — jak w core */
  h1?: string | null;
}

const Details: FC<IProps> = ({
  product,
  scrollToDescription,
  hideTitleAndMetadata = false,
  h1,
}) => {
  const navigate = useAppNavigate();
  const { t } = useTranslation();

  // pokazywanie popupa z GPSR
  const [technicalInformationVisible, setTechnicalInformationVisible] = useState(false);

  // renderowanie atrybutu
  const renderTechnicalAttributeValue = (item: ITechnicalAttribute) =>
    item.type === 'boolean' ? (
      item.value ? (
        <Check2 className={styles.green} />
      ) : (
        <X className={styles.red} />
      )
    ) : (
      item.value
    );

  // Funkcja zwracająca ikonę SVG dla danego atrybutu
  const getAttributeIcon = (attributeName: string | undefined) => {
    if (!attributeName) return null;

    const iconMap: Record<string, JSX.Element> = {
      DO_ZAMROZENIA: <DoZamrozeniaIcon />,
      DO_MIKROFALOWKI: <DoMikrofalowkiIcon />,
      DO_CIEPLYCH_PRODUKTOW: <DoCieplychProduktowIcon />,
      TYLKO_DO_ZIMNYCH_PRODUKTOW: <TylkoDoZimnychProduktowIcon />,
      DO_ZMYWARKI: <DoZmywarkiIcon />,
      BIODEGRADOWALNE: <BiodegradowalneIcon />,
      BIO: <BiodegradowalneIcon />, // alias dla BIO
      DO_RECYKLINGU: <DoRecyklinguIcon />,
      POD_NADRUK: <PodNadrukIcon />,
      Z_RECYKLINGU: <ZRecyklinguIcon />,
      Z_ROSLIN: <ZRoslinIcon />,
      DO_POSADZEK: <DoPosadzekIcon />,
      DO_SANITARIATOW: <DoSanitariatowIcon />,
      NEUTRALIZACJA_ZAPACHOW_I_ODSWIEZANIE: <NeutralizacjaZapachowIcon />,
      POWIERZCHNIE_SZKLANE: <PowierzchnieSzlaneIcon />,
      DO_DREWNA: <DoDrewnaIcon />,
      DO_STALI_NIERDZEWNEJ: <DoStaliNierdzewnejIcon />,
      DLA_GASTRONOMII: <DlaGastronomiiIcon />,
      DYWANY_I_WYKLADZINY: <DywanyIWykadzinyIcon />,
      PRZEMYSL: <PrzemyslIcon />,
      DEZYNFEKCJA: <DezynfekcjaIcon />,
      MYCIE_RECZNE: <MycieReczneIcon />,
      MYCIE_MASZYNOWE: <MycieMaszynoweIcon />,
      MYCIE_MOPEM: <MycieMopemIcon />,
      SPRYSKIWACZ: <SpryskiwaczIcon />,
      DO_SYSTEMU_DOZOWANIA: <DoSystemuDozowaniaIcon />,
    };

    return iconMap[attributeName] || null;
  };

  const renderGpsrContent = () => {
    const gpsrData = product.gpsr_supplier_info;
    return (
      <div>
        <table>
          <tr>
            <th />
            <th />
          </tr>
          <tr className={styles.gpsrDataRow}>
            <td className={styles.gpsrDataCol}>
              <Trans>Nazwa</Trans>:
            </td>
            <td className={styles.gpsrDataCol}>{gpsrData?.registered_trade_name}</td>
          </tr>
          <tr className={styles.gpsrDataRow}>
            <td className={styles.gpsrDataCol}>
              <Trans>NIP</Trans>:
            </td>
            <td className={styles.gpsrDataCol}>{gpsrData?.tax_number}</td>
          </tr>
          <tr className={styles.gpsrDataRow}>
            <td className={styles.gpsrDataCol}>
              <Trans>Email</Trans>:
            </td>
            <td className={styles.gpsrDataCol}>
              <a href={`mailto:${gpsrData?.email}`}>{gpsrData?.email}</a>
            </td>
          </tr>
          <tr className={styles.gpsrDataRow}>
            <td className={styles.gpsrDataCol}>
              <Trans>Telefon</Trans>:
            </td>
            <td className={styles.gpsrDataCol}>{gpsrData?.phone}</td>
          </tr>
          <tr className={styles.gpsrDataRow}>
            <td className={classnames(styles.gpsrDataCol, styles.gpsrAddress)}>
              <div>
                <Trans>Dane adresowe</Trans>:
              </div>
            </td>
            <td className={styles.gpsrDataCol}>
              <div>
                {gpsrData?.address?.street} {gpsrData?.address?.house_number}
                {gpsrData?.address?.apartment_number && `/${gpsrData?.address?.apartment_number}`}
                <br />
                {gpsrData?.address?.postal_code}, {gpsrData?.address?.city}{' '}
                {gpsrData?.address?.country}
              </div>
            </td>
          </tr>
        </table>
      </div>
    );
  };

  return (
    <div
      className={classnames(styles.productDetails, 'StylePath-Pages-Product-components-Details')}>
      <div className={styles.mainContent}>
        {!hideTitleAndMetadata && (
          <>
            <div className={styles.titleWrapper}>
              <h1 itemProp="name" className={styles.title}>
                {h1 || product?.product_basic_full_info?.title}
              </h1>
            </div>

            <div className={styles.metadata}>
              <div className={styles.metadataItem}>
                <span className={styles.metadataLabel}>
                  <Trans>Kategoria</Trans>:
                </span>
                <strong className={styles.metadataValue}>
                  <Link to={`/${product?.product_basic_full_info?.category_url}`}>
                    {product?.product_basic_full_info?.category_name}
                  </Link>
                </strong>
              </div>
              <div className={styles.metadataItem}>
                <span className={styles.metadataLabel}>
                  <Trans>INDEX</Trans>:
                </span>
                <strong className={styles.metadataValue}>
                  {product?.product_basic_full_info?.index}
                </strong>
              </div>
              {product?.product_basic_full_info?.producer_name && (
                <div className={styles.metadataItem}>
                  <span className={styles.metadataLabel}>
                    <Trans>Producent</Trans>:
                  </span>
                  <strong className={styles.metadataValue} itemProp="name">
                    {product?.product_basic_full_info?.producer_name}
                  </strong>
                </div>
              )}
            </div>
          </>
        )}

        <div className={styles.description}>
          <span className={styles.label}>
            <Trans>Opis</Trans>
          </span>
          <div itemProp="description" className={styles.text}>
            {product.product_basic_full_info?.short_description}
          </div>
        </div>

        <div className={styles.actions}>
          <button onClick={scrollToDescription} className={styles.scrollToDescription}>
            <Trans>Przewiń do pełnego opisu</Trans> <ChevronDown />
          </button>
        </div>

        {/* Atrybuty produktu z ikonami */}
        {product?.product_technical_attributes_full_info?.attributes && (
          <div className={styles.productAttributes}>
            {product.product_technical_attributes_full_info.attributes
              .filter((attr) => hasAttributeIcon(attr.name))
              .map((attr) => {
                const iconComponent = getAttributeIcon(attr.name);
                if (!iconComponent) return null;
                const translationKey = `product.attribute.${attr.name}`;
                return (
                  <div key={attr.name} className={styles.attributeItem}>
                    <div className={styles.attributeIcon}>{iconComponent}</div>
                    <div className={styles.attributeText}>{t(translationKey)}</div>
                  </div>
                );
              })}
          </div>
        )}

        {/* Adnotacja */}
        {product?.product_basic_full_info?.annotation && (
          <div className={styles.annotation}>
            <div className={styles.annotationText}>
              {product.product_basic_full_info.annotation}
            </div>
          </div>
        )}
      </div>

      {technicalInformationVisible && (
        <Modal
          title={t('Informacja o dostawcy')}
          onClose={() => {
            setTechnicalInformationVisible(false);
          }}>
          {renderGpsrContent()}
          <div className={styles.confirmationModalActions}>
            <Button onClick={() => setTechnicalInformationVisible(false)}>
              <Trans>Zamknij</Trans>
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Details;
